import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PersonStatus, type SimilarPersonDto } from '@ciimar/shared';
import { api } from '../api/client';
import { useT } from '../lib/i18n';

/**
 * A small indicator shown while entering a new collaborator's name: when the
 * name is identical or similar to existing collaborators, it surfaces a chip
 * that opens a floating list of matches — without disturbing the form (the
 * matches open in a new tab). Silent when there are no matches.
 */
export function SimilarNames({ name, excludeId }: { name: string; excludeId?: string }) {
  const t = useT();
  const [matches, setMatches] = useState<SimilarPersonDto[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = name.trim();
    if (q.length < 3) {
      setMatches([]);
      return;
    }
    let active = true;
    const h = setTimeout(async () => {
      try {
        const r = await api.similarPeople(q);
        if (active) setMatches(excludeId ? r.filter((p) => p.id !== excludeId) : r);
      } catch {
        if (active) setMatches([]);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(h);
    };
  }, [name, excludeId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (matches.length === 0) return null;
  const n = matches.length;

  return (
    <div className="simnames" ref={ref}>
      <button
        type="button"
        className="simnames-chip"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.3 3.9 1.8 18a1.8 1.8 0 0 0 1.5 2.7h17.4A1.8 1.8 0 0 0 22.2 18L13.7 3.9a1.8 1.8 0 0 0-3.4 0zM12 9v4M12 17h.01" />
        </svg>
        {n === 1 ? t('people.similar.one') : t('people.similar.many', { n })}
      </button>
      {open && (
        <div className="simnames-pop">
          <div className="simnames-head">{t('people.similar.heading')}</div>
          <ul>
            {matches.map((p) => (
              <li key={p.id}>
                <Link to={`/people/${p.id}`} target="_blank" rel="noreferrer">
                  <span className="simnames-name">{p.fullName}</span>
                  <span className="muted">
                    {[
                      p.code,
                      p.status === PersonStatus.Active ? t('shared.active') : t('shared.inactive'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
